/**
 * Route table and path builders for the C3 console (docs/c3/decisions/001-routing.md).
 *
 * The console is one page at `/`; its state lives in the query string. Only the dedicated
 * views below have a path of their own. Build links with these helpers so no component
 * assembles a URL by hand.
 */

export const ROUTES = {
  console: '/',
  sources: '/sources',
  roi: '/roi',
  tests: '/tests',
  settings: '/settings',
  /** `/settings` and `/settings/:section`. */
  settingsSection: '/settings/:section?',
  /** A campaign, or a lone incident (a campaign of one). */
  campaign: '/campaigns/:id',
  /** Kept so old links work: it redirects to the campaign of that incident. */
  incident: '/incidents/:id',
  entity: '/entities/:id',
} as const;

/** Section shown when `/settings` has no section: the screen of boxes that leads to the others. */
export const DEFAULT_SETTINGS_SECTION = 'home';

/** The id is a campaign's, or an incident's: an incident opens the campaign it belongs to. */
export function campaignPath(id: string): string {
  return `/campaigns/${encodeURIComponent(id)}`;
}

export function entityPath(id: string): string {
  return `/entities/${encodeURIComponent(id)}`;
}

export function settingsPath(section?: string): string {
  return !section || section === DEFAULT_SETTINGS_SECTION
    ? ROUTES.settings
    : `${ROUTES.settings}/${encodeURIComponent(section)}`;
}

/**
 * `connect` opens the connect-source dialog, with `provider` and `name` already in it when an application was picked from
 * the menu of available sources; `edit` opens that source for editing.
 */
export interface SourcesIntent {
  connect?: boolean;
  editId?: string;
  provider?: string;
  name?: string;
}

export function sourcesPath(intent?: SourcesIntent | null): string {
  const params = new URLSearchParams();
  if (intent?.connect) params.set('connect', '1');
  if (intent?.connect && intent.provider) params.set('provider', intent.provider);
  if (intent?.connect && intent.name) params.set('name', intent.name);
  if (intent?.editId) params.set('edit', intent.editId);
  const qs = params.toString();
  return qs ? `${ROUTES.sources}?${qs}` : ROUTES.sources;
}

export function sourcesIntentFromParams(params: URLSearchParams): SourcesIntent | null {
  const editId = params.get('edit') || undefined;
  const connect = params.get('connect') === '1';
  const provider = params.get('provider') || undefined;
  const name = params.get('name') || undefined;
  if (!connect && !editId) return null;
  return {
    ...(connect ? { connect } : {}),
    ...(connect && provider ? { provider } : {}),
    ...(connect && name ? { name } : {}),
    ...(editId ? { editId } : {}),
  };
}

/** Paths still written for a dark surface; they render in the Dim theme until they are rebuilt. */
const LEGACY_DARK_BASES = ['/incidents', '/roi', '/tests'];

function underBase(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

export function isLegacyDarkPath(pathname: string): boolean {
  return LEGACY_DARK_BASES.some((base) => underBase(pathname, base));
}

/** Views that fill the page width and carry their own padding; the rest sit in a padded frame. */
export function isFullBleedPath(pathname: string): boolean {
  return (
    pathname === ROUTES.console ||
    underBase(pathname, ROUTES.settings) ||
    underBase(pathname, ROUTES.sources) ||
    // The campaign screen is a band with its own padding: in the padded frame it sat 52 px in, against 28 for the others.
    underBase(pathname, '/campaigns')
  );
}
